import { submitOrder } from 'axios/orderApi';
import useApi from 'hooks/useApi';
import { OrderDetailsGeneralInfo, OrderScoring, RequestDocsInfo } from 'models/api/order';
import {
    createContext,
    Dispatch,
    RefObject,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

type FormContextType = {
    isDirty: boolean;
    setDirty: Dispatch<SetStateAction<boolean>>;
    scoring: OrderScoring | null;
    setScoring: Dispatch<SetStateAction<OrderScoring | null>>;
    requestedDocs: RequestDocsInfo[];
    setRequestedDocs: Dispatch<SetStateAction<RequestDocsInfo[]>>;
    orderDetails: OrderDetailsGeneralInfo | null;
    submitOrderMutation: UseMutationResult<any, unknown, string, unknown>;
    checksTabFormRef: RefObject<HTMLFormElement>;
};

const OrderInfoContext = createContext<FormContextType>({} as FormContextType);

export type OrderInfoProviderProps = {
    orderDetails: OrderDetailsGeneralInfo | null;
    children: React.ReactNode;
    onSubmitOrderSuccess: () => void;
};

const OrderInfoProvider = ({
    children,
    orderDetails,
    onSubmitOrderSuccess,
}: OrderInfoProviderProps) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const [isDirty, setDirty] = useState(false);
    const [scoring, setScoring] = useState<OrderScoring | null>(null);
    const [requestedDocs, setRequestedDocs] = useState<RequestDocsInfo[]>([]);
    const [orderDetailsInfo, setOrderDetails] = useState<OrderDetailsGeneralInfo | null>(
        orderDetails
    );
    const checksTabFormRef = useRef<HTMLFormElement>(null);

    useEffect(() => setOrderDetails(orderDetails), [orderDetails]);

    const submitOrderMutation = useMutation(
        'submitOrderMutation',
        (submitType: string) => {
            if (orderDetails) {
                return submitOrder(api, orderDetails.id, submitType, scoring);
            }
            return Promise.resolve(null);
        },
        {
            onSuccess: () => {
                if (onSubmitOrderSuccess) {
                    queryClient.invalidateQueries(['orderStatusCount']);
                    queryClient.invalidateQueries([['order']]);
                    onSubmitOrderSuccess();
                }
            },
        }
    );

    return (
        <OrderInfoContext.Provider
            value={{
                isDirty,
                setDirty,
                scoring,
                setScoring,
                requestedDocs,
                setRequestedDocs,
                orderDetails: orderDetailsInfo,
                submitOrderMutation,
                checksTabFormRef,
            }}
        >
            {children}
        </OrderInfoContext.Provider>
    );
};

const useOrderInfoContext = () => useContext<FormContextType>(OrderInfoContext);

export { OrderInfoProvider, useOrderInfoContext };
